import type { ResponsiveValue } from '@chakra-ui/react';
import { Box, Container, Flex, Image, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { SplitHeading } from '../components/SplitHeading';
import { ScrollDownLink } from '../components/ScrollDownLink';

type CardPos = {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  /** Explicit width (e.g. "44%" of the phone box) so callouts match Figma proportions. */
  w?: string;
};

/** A connector line linking a callout card to the phone: image + absolute position + width. */
interface Conn extends CardPos {
  src: string;
  w: string;
}

interface FeatureShowcaseProps {
  id: string;
  ns: 'music' | 'automation';
  /** id of the next section the scroll-down cue jumps to. */
  nextAnchor: string;
  phoneSrc: string;
  phoneMaxW: ResponsiveValue<string>;
  card1Pos: CardPos;
  card2Pos: CardPos;
  conn1: Conn;
  conn2: Conn;
  /** Filled dots terminating each connector line on the phone (Figma: 6px #573447),
      positioned as percentages of the phone box. */
  dots?: { top: string; left: string }[];
  /** Optional section background gradient (Automation uses the cream→peach wash). */
  bgGradient?: string;
}

function Connector({ src, w, ...pos }: Conn) {
  return (
    <Image
      src={src}
      alt=""
      position="absolute"
      {...pos}
      w={w}
      display={{ base: 'none', xl: 'block' }}
      pointerEvents="none"
      draggable={false}
    />
  );
}

function FloatingCard({ title, sub, pos }: { title: string; sub: string; pos: CardPos }) {
  const { w, ...edges } = pos;
  return (
    <Box
      position="absolute"
      {...edges}
      w={w ?? '200px'}
      bg="white"
      borderRadius="14px"
      px="16px"
      py="11px"
      boxShadow="0 14px 36px rgba(39,13,23,0.14)"
      display={{ base: 'none', xl: 'block' }}
    >
      <Text fontSize="sm" fontWeight={600} color="onboarding.deepPlum" lineHeight={1.2}>
        {title}
      </Text>
      <Text fontSize="xs" fontWeight={300} color="onboarding.deepPlum" lineHeight={1.2}>
        {sub}
      </Text>
    </Box>
  );
}

export function FeatureShowcase({ id, ns, nextAnchor, phoneSrc, phoneMaxW, card1Pos, card2Pos, conn1, conn2, dots, bgGradient }: FeatureShowcaseProps) {
  const { t } = useTranslation();
  const k = (s: string) => t(`landing.showcase.${ns}.${s}`);

  return (
    <Box as="section" id={id} bg={bgGradient ? undefined : 'white'} bgGradient={bgGradient} overflow="hidden">
      <Container maxW="1240px" py={{ base: 14, lg: 20 }}>
        <Flex direction={{ base: 'column', lg: 'row' }} align="center" gap={{ base: 10, lg: 12 }}>
          <Box flex="1" maxW={{ lg: '520px' }}>
            <Text
              fontFamily="heading"
              fontSize="display.lg"
              fontWeight={600}
              color="#E5D8D7"
              lineHeight={1}
              mb={{ base: '16px', lg: '40px' }}
            >
              {k('num')}
            </Text>
            <Stack spacing="22px" align="flex-start">
              <SplitHeading
                as="h2"
                text={k('title')}
                fontFamily="heading"
                fontWeight={700}
                fontSize="display.lg"
                lineHeight={1.12}
                letterSpacing="-0.01em"
                color="onboarding.deepPlum"
              />
              <Text fontSize="md" color="onboarding.deepPlum" lineHeight={1.6}>
                {k('body')}
              </Text>
              <Box pt="12px">
                <ScrollDownLink targetId={nextAnchor} label={t('landing.showcase.scrollDown')} />
              </Box>
            </Stack>
          </Box>

          <Flex flex="1" justify="center" w="full">
            {/* The callouts and connectors are positioned relative to THIS box,
                which shrink-wraps the phone (w:full capped at the phone's max).
                So the whole cluster scales as one with the phone and the
                connectors stay attached at any width — previously they were
                anchored to the wider flex column and drifted off the phone.
                All offsets in card*Pos / conn* are percentages of this box. */}
            <Box position="relative" w="full" maxW={phoneMaxW}>
              <Image src={phoneSrc} alt="" w="full" display="block" draggable={false} />
              <Connector {...conn1} />
              <Connector {...conn2} />
              {dots?.map((dp, i) => (
                <Box
                  key={i}
                  position="absolute"
                  top={dp.top}
                  left={dp.left}
                  transform="translate(-50%, -50%)"
                  w="7px"
                  h="7px"
                  borderRadius="full"
                  bg="onboarding.midRose2"
                  display={{ base: 'none', xl: 'block' }}
                  pointerEvents="none"
                />
              ))}
              <FloatingCard title={k('card1.title')} sub={k('card1.sub')} pos={card1Pos} />
              <FloatingCard title={k('card2.title')} sub={k('card2.sub')} pos={card2Pos} />
            </Box>
          </Flex>
        </Flex>
      </Container>
    </Box>
  );
}
